import { generatePath } from 'react-router-dom';
import type { LegalDocKey } from '@escala/domain/legal';

/**
 * Caminhos das rotas — fonte única para `routes.tsx` e para todo link da aplicação.
 *
 * Link nunca monta URL com string solta: renomear uma rota deixaria links quebrados
 * espalhados, sem erro de tipo nem de teste apontando onde.
 */
export const paths = {
  home: '/',
  /** "Minha agenda" de quem é da equipe (o Membro a tem no início). */
  agenda: '/agenda',
  /** Calendário: os eventos da igreja, as escalas e o público dos cultos. */
  schedules: '/calendario',
  generate: '/calendario/gerar',
  /** Membresia: indicadores da comunidade, público dos cultos, serviço nos ministérios e distribuição geográfica. */
  membership: '/membresia',
  events: '/eventos',
  ministries: '/ministerios',
  ministry: '/ministerios/:ministryId',
  members: '/membros',
  /** Cadastro de membro: o perfil vazio, como rascunho, até "Cadastrar membro". */
  newMember: '/membros/novo',
  member: '/membros/:memberId',
  me: '/perfil',
  notifications: '/notificacoes',
  accesses: '/acessos',
  /** Organizações (só o Administrador da plataforma). */
  orgs: '/organizacoes',
  settings: '/configuracoes',
} as const;

/**
 * Endereços antigos: o Calendário (era "Escalas") e a Distribuição (hoje a aba Serviço da
 * Membresia) continuam abrindo, redirecionados, para links salvos e favoritos não quebrarem.
 */
export const legacyPaths = {
  schedules: '/escalas',
  generate: '/escalas/gerar',
  distribution: '/distribuicao',
} as const;

/** Abas da Membresia (`?aba=`). */
export type MembershipTab =
  'visao' | 'presenca' | 'servico' | 'visitantes' | 'geografia' | 'relatorios' | 'aniversariantes';

/**
 * Membresia numa aba (`?aba=presenca`), com o filtro de situação da Presença nos cultos ou,
 * em Visitantes, só os acompanhamentos atrasados (`?filtro=atrasados`).
 */
export function membershipPath(
  tab: MembershipTab = 'visao',
  options: { status?: 'registrados' | 'pendentes'; overdue?: boolean } = {}
): string {
  const search = new URLSearchParams();

  if (tab !== 'visao') search.set('aba', tab);
  if (options.status) search.set('situacao', options.status);
  if (options.overdue) search.set('filtro', 'atrasados');

  const query = search.toString();

  return query ? `${paths.membership}?${query}` : paths.membership;
}

/** Solicitações de cadastro abertas na página Membros (o aviso de cadastro novo leva aqui). */
export const registrationsPath = '/membros?solicitacoes=1';

/**
 * Telas de acesso, fora da moldura e do roteador: os links dos e-mails da API apontam
 * para elas (`server/src/auth/auth.service.ts`).
 */
export const authPaths = {
  invite: '/convite',
  reset: '/redefinir-senha',
} as const;

/** Configurações › Privacidade: os pedidos dos titulares (o aviso leva até lá). */
export const privacyRequestsPath = `${paths.settings}?privacidade=1`;

/**
 * Os documentos legais públicos (fora da sessão, nos dois modos): o link dos e-mails, das
 * lojas e de quem quiser ler antes de entrar.
 */
export const legalPaths = {
  privacy: '/privacidade',
  terms: '/termos',
  cookies: '/cookies',
} as const satisfies Record<LegalDocKey, string>;

/** O documento do endereço (`/privacidade`, com ou sem o prefixo do build), ou `null`. */
export function legalDocOf(pathname: string): LegalDocKey | null {
  const path = pathname.replace(/\/+$/, '');
  const found = (Object.entries(legalPaths) as [LegalDocKey, string][]).find(([, p]) => path.endsWith(p));

  return found ? found[0] : null;
}

export type ProfileTab = 'info' | 'ministerios' | 'disponibilidade' | 'historico' | 'acesso';

/** Perfil de um membro, opcionalmente já numa aba (`?aba=historico`). */
export function memberPath(memberId: string, tab?: ProfileTab): string {
  const path = generatePath(paths.member, { memberId });

  return tab && tab !== 'info' ? `${path}?aba=${tab}` : path;
}

export function ministryPath(ministryId: string): string {
  return generatePath(paths.ministry, { ministryId });
}

/** Visões de Escalas; `historico` (registro de quem serviu) é só da equipe. */
export type ScheduleView = 'mes' | 'semana' | 'evento' | 'historico';

/** Escalas com visão e filtros na URL, para o dashboard levar direto ao recorte. */
export function schedulesPath(
  params: { view?: ScheduleView; status?: string; ministry?: string } = {}
): string {
  const search = new URLSearchParams();

  if (params.view) search.set('visao', params.view);
  if (params.status) search.set('status', params.status);
  if (params.ministry) search.set('ministerio', params.ministry);

  const query = search.toString();

  return query ? `${paths.schedules}?${query}` : paths.schedules;
}
